<template>
    <div class="working-recorder">
        <h3>按住录音器</h3>
        <div class="controls">
            <button
                :class="['record-btn', { 
                    'recording': isRecording, 
                    'connecting': isConnecting,
                    'connecting-websocket': isConnectingWebSocket
                }]"
                @mousedown="handleMouseDown"
                @mouseup="handleMouseUp"
                @mouseleave="handleMouseLeave"
                @touchstart="handleTouchStart"
                @touchend="handleTouchEnd"
                :disabled="isConnecting || isConnectingWebSocket"
            >
                <div class="button-content">
                    <img 
                        :src="microphoneIcon" 
                        alt="麦克风" 
                        class="microphone-icon"
                    />
                    <span class="button-text">{{ getButtonText() }}</span>
                </div>
            </button>
        </div>

        <div class="status-info">
            <div v-if="isConnectingWebSocket" class="status connecting">
                <div class="connection-progress">
                    <div class="progress-bar">
                        <div class="progress-fill" :style="{ width: connectionProgress + '%' }"></div>
                    </div>
                    <span class="progress-text">连接中... {{ Math.round(connectionProgress) }}%</span>
                </div>
            </div>
            <div v-else-if="isWebSocketConnected" class="status connected">
                ✅ WebSocket已连接
            </div>
            <div v-else class="status disconnected">
                ❌ WebSocket未连接
            </div>
            <div v-if="isRecording" class="recording-info">
                <span class="timer">{{ formatTime(recordingTime) }}</span>
            </div>
        </div>

        <div class="result-container">
            <h4>识别结果：</h4>
            <div class="result-text">{{ resultText || '暂无识别结果' }}</div>
        </div>

    </div>
</template>

<script>
export default {
    name: 'WorkingRecorder',
    data() {
        return {
            isRecording: false,
            isConnecting: false,
            isConnectingWebSocket: false,
            recordingTime: 0,
            resultText: '',
            recorder: null,
            speechService: null,
            timeInterval: null,
            isWebSocketConnected: false,
            isButtonPressed: false,
            connectionProgress: 0
        }
    },
    computed: {
        microphoneIcon() {
            if (this.isRecording || this.isButtonPressed) {
                return require('@/assets/images/svg/microphoneClick.svg')
            } else {
                return require('@/assets/images/svg/microphone.svg')
            }
        }
    },
    async mounted() {
        // 等待RecorderManager加载
        await this.waitForRecorderManager()
        this.initServices()
        // 不再自动连接WebSocket，改为按住时连接
    },
    beforeUnmount() {
        this.cleanup()
    },
    methods: {
        async waitForRecorderManager() {
            return new Promise((resolve) => {
                const check = () => {
                    if (window.RecorderManager) {
                        console.log('RecorderManager已加载')
                        resolve()
                    } else {
                        console.log('等待RecorderManager...')
                        setTimeout(check, 100)
                    }
                }
                check()
            })
        },

        initServices() {
            // 预加载SpeechService - 提高后续连接速度
            if (!this.speechService) {
                import('../services/speechService.js').then(module => {
                    this.speechService = new module.SpeechService()
                    console.log('SpeechService预加载完成')
                }).catch(error => {
                    console.error('SpeechService加载失败:', error)
                })
            }
        },

        // 连接WebSocket（按住时调用）
        async connectWebSocket() {
            if (this.isConnectingWebSocket) {
                return
            }

            // 先关闭之前的连接（如果有的话）
            if (this.speechService && this.speechService.ws) {
                console.log('关闭之前的WebSocket连接...')
                this.speechService.close()
            }

            this.isConnectingWebSocket = true
            this.isWebSocketConnected = false
            this.connectionProgress = 0

            // 连接超时机制 - 最多等待8秒
            const connectionTimeout = setTimeout(() => {
                if (this.isConnectingWebSocket) {
                    console.error('WebSocket连接超时')
                    this.isConnectingWebSocket = false
                    this.connectionProgress = 0
                    clearInterval(progressInterval)
                    this.showConnectionError('连接超时，请重试')
                }
            }, 8000)

            // 优化的连接进度动画 - 更快更真实
            const progressInterval = setInterval(() => {
                if (this.connectionProgress < 95) {
                    // 前80%快速增长，后15%缓慢增长
                    if (this.connectionProgress < 80) {
                        this.connectionProgress += Math.random() * 15 + 10
                    } else {
                        this.connectionProgress += Math.random() * 5 + 2
                    }
                }
            }, 50) // 更快的更新频率

            try {
                // 并行处理：同时等待SpeechService和开始连接
                console.log('开始建立新的WebSocket连接...')
                
                // 如果SpeechService还没准备好，先等待
                if (!this.speechService) {
                    await this.waitForSpeechService()
                }
                
                this.speechService.connectWebSocket(
                    // onMessage
                    (text, jsonData) => {
                        this.resultText = text
                    },
                    // onOpen
                    () => {
                        console.log('WebSocket连接成功')
                        this.isWebSocketConnected = true
                        this.isConnectingWebSocket = false
                        this.connectionProgress = 100
                        clearInterval(progressInterval)
                        clearTimeout(connectionTimeout) // 清除超时定时器
                        // 连接成功后，如果用户还在按住按钮，则开始录音
                        if (this.isButtonPressed) {
                            this.startRecording()
                        }
                    },
                    // onError
                    (error) => {
                        console.error('WebSocket错误:', error)
                        this.isWebSocketConnected = false
                        this.isConnectingWebSocket = false
                        this.connectionProgress = 0
                        clearInterval(progressInterval)
                        clearTimeout(connectionTimeout) // 清除超时定时器
                        this.showConnectionError('连接失败，请重试')
                    },
                    // onClose
                    (event) => {
                        console.log('WebSocket连接关闭:', event.code, event.reason)
                        this.isWebSocketConnected = false
                        this.isConnectingWebSocket = false
                        this.connectionProgress = 0
                        clearInterval(progressInterval)
                        clearTimeout(connectionTimeout) // 清除超时定时器
                    }
                )
            } catch (error) {
                console.error('连接WebSocket失败:', error)
                this.isConnectingWebSocket = false
                this.connectionProgress = 0
                clearInterval(progressInterval)
                clearTimeout(connectionTimeout) // 清除超时定时器
                this.showConnectionError('连接失败，请重试')
            }
        },

        // 等待SpeechService初始化 - 优化等待时间
        waitForSpeechService() {
            return new Promise((resolve, reject) => {
                let attempts = 0
                const maxAttempts = 50 // 最多等待5秒
                
                const checkService = () => {
                    if (this.speechService) {
                        resolve()
                    } else if (attempts >= maxAttempts) {
                        reject(new Error('SpeechService初始化超时'))
                    } else {
                        attempts++
                        setTimeout(checkService, 50) // 更频繁的检查
                    }
                }
                checkService()
            })
        },

        // 显示连接错误
        showConnectionError(message) {
            this.resultText = message
            setTimeout(() => {
                this.resultText = ''
            }, 3000)
        },

        // 检查麦克风权限
        async checkMicrophonePermission() {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
                console.log('麦克风权限检查通过')
                stream.getTracks().forEach(track => track.stop()) // 停止测试流
                return true
            } catch (error) {
                console.error('麦克风权限检查失败:', error)
                this.showConnectionError('无法访问麦克风，请检查权限设置')
                return false
            }
        },

        getButtonText() {
            if (this.isConnectingWebSocket) {
                return '连接中...'
            } else if (this.isRecording) {
                return '松开结束'
            } else {
                return '按住说话'
            }
        },


        formatTime(seconds) {
            const mins = Math.floor(seconds / 60)
            const secs = seconds % 60
            return `${mins}:${secs.toString().padStart(2, '0')}`
        },

        // 鼠标按下事件
        handleMouseDown(event) {
            event.preventDefault()
            if (!this.isButtonPressed && !this.isRecording && !this.isConnectingWebSocket) {
                this.isButtonPressed = true
                // 每次按住都重新连接WebSocket
                this.connectWebSocket()
            }
        },

        // 鼠标松开事件
        handleMouseUp(event) {
            event.preventDefault()
            if (this.isButtonPressed && this.isRecording) {
                this.isButtonPressed = false
                this.stopRecording()
            }
        },

        // 鼠标离开事件
        handleMouseLeave(event) {
            if (this.isButtonPressed && this.isRecording) {
                this.isButtonPressed = false
                this.stopRecording()
            }
        },

        // 触摸开始事件
        handleTouchStart(event) {
            event.preventDefault()
            if (!this.isButtonPressed && !this.isRecording && !this.isConnectingWebSocket) {
                this.isButtonPressed = true
                // 每次按住都重新连接WebSocket
                this.connectWebSocket()
            }
        },

        // 触摸结束事件
        handleTouchEnd(event) {
            event.preventDefault()
            if (this.isButtonPressed && this.isRecording) {
                this.isButtonPressed = false
                this.stopRecording()
            }
        },

        async startRecording() {
            // 检查WebSocket是否已连接
            if (!this.isWebSocketConnected || this.isRecording || this.isConnectingWebSocket) {
                console.log('录音启动条件不满足:', {
                    isWebSocketConnected: this.isWebSocketConnected,
                    isRecording: this.isRecording,
                    isConnectingWebSocket: this.isConnectingWebSocket
                })
                return
            }

            try {
                console.log('开始录音...')
                
                // 检查麦克风权限
                const hasPermission = await this.checkMicrophonePermission()
                if (!hasPermission) {
                    this.stopRecording()
                    return
                }

                this.isConnecting = true
                this.isRecording = true
                this.recordingTime = 0

                // 重置语音服务结果
                if (this.speechService) {
                    this.speechService.reset()
                    this.resultText = ''
                }

                // 直接启动录音器，因为WebSocket已经连接
                await this.startRecorder()
            } catch (error) {
                console.error('录音启动失败:', error)
                this.stopRecording()
            }
        },

        async startRecorder() {
            if (window.RecorderManager) {
                console.log('创建RecorderManager实例...')
                try {
                    this.recorder = new window.RecorderManager('./dist')
                    console.log('RecorderManager实例创建成功')
                } catch (error) {
                    console.error('创建RecorderManager失败:', error)
                    this.showConnectionError('录音器初始化失败')
                    return
                }

                this.recorder.onStart = () => {
                    console.log('录音开始 - onStart 回调触发')
                    this.isConnecting = false
                    this.startTimer()
                }

                this.recorder.onFrameRecorded = ({ frameBuffer, isLastFrame }) => {
                    console.log('收到音频帧:', frameBuffer.byteLength, 'bytes, isLastFrame:', isLastFrame)
                    if (this.speechService && this.speechService.ws && this.speechService.ws.readyState === WebSocket.OPEN) {
                        console.log('发送音频数据到WebSocket...')
                        this.speechService.sendAudioData(frameBuffer, isLastFrame)
                    } else {
                        console.warn('WebSocket未连接或speechService不可用', {
                            speechService: !!this.speechService,
                            ws: !!this.speechService?.ws,
                            readyState: this.speechService?.ws?.readyState
                        })
                    }
                }

                this.recorder.onStop = () => {
                    console.log('录音停止回调')
                    this.stopTimer()
                    this.isRecording = false
                    this.isConnecting = false
                    // 发送结束信号（但不关闭WebSocket连接）
                    if (this.speechService && this.speechService.ws && this.speechService.ws.readyState === WebSocket.OPEN) {
                        console.log('发送录音结束信号...')
                        this.speechService.sendAudioData(new ArrayBuffer(0), true)
                    }
                }

                console.log('启动录音器...')
                try {
                    await this.recorder.start({
                        sampleRate: 16000,
                        frameSize: 1280
                    })
                    console.log('录音器启动完成')
                } catch (error) {
                    console.error('录音器启动失败:', error)
                    this.showConnectionError('录音器启动失败，请检查麦克风权限')
                    this.stopRecording()
                }
            } else {
                console.error('RecorderManager不可用')
                this.showConnectionError('录音器不可用，请检查页面资源')
            }
        },

        stopRecording() {
            console.log('停止录音...')

            // 重置按钮状态
            this.isButtonPressed = false

            // 先停止录音器
            if (this.recorder) {
                this.recorder.stop()
                this.recorder = null
            }

            // 发送结束信号给WebSocket
            if (this.speechService && this.speechService.ws && this.speechService.ws.readyState === WebSocket.OPEN) {
                this.speechService.sendAudioData(new ArrayBuffer(0), true)
                // 延迟关闭WebSocket连接，确保结束信号发送完成
                setTimeout(() => {
                    console.log('关闭WebSocket连接...')
                    this.speechService.close()
                    this.isWebSocketConnected = false
                }, 500)
            }

            this.stopTimer()
            this.isRecording = false
            this.isConnecting = false
        },

        startTimer() {
            this.timeInterval = setInterval(() => {
                this.recordingTime++
                if (this.recordingTime >= 60) {
                    this.stopRecording()
                }
            }, 1000)
        },

        stopTimer() {
            if (this.timeInterval) {
                clearInterval(this.timeInterval)
                this.timeInterval = null
            }
        },

        cleanup() {
            this.stopRecording()
            // 关闭WebSocket连接
            if (this.speechService) {
                this.speechService.close()
            }
        }
    }
}
</script>

<style scoped>
.working-recorder {
    padding: 20px;
    border: 1px solid #e0e0e0;
    border-radius: 15px;
    background: #fafafa;
    margin: 20px 0;
}

.controls {
    margin: 15px 0;
}

.record-btn {
    padding: 20px 40px;
    border: none;
    border-radius: 50px;
    font-size: 1.2rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 180px;
    min-height: 60px;
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    display: flex;
    align-items: center;
    justify-content: center;
}

.button-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
}

.microphone-icon {
    width: 32px;
    height: 32px;
    transition: all 0.2s ease;
}

.button-text {
    font-size: 0.9rem;
    font-weight: 500;
}

.record-btn:not(:disabled) {
    background: #4CAF50;
    color: white;
    box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.record-btn:not(:disabled):active,
.record-btn.recording {
    background: #f44336;
    transform: scale(0.95);
    box-shadow: 0 2px 8px rgba(244, 67, 54, 0.4);
}

.record-btn:disabled {
    background: #9e9e9e;
    color: white;
    cursor: not-allowed;
    box-shadow: none;
}

.record-btn.connecting-websocket {
    background: #ff9800;
    color: white;
    animation: pulse-connecting 1.5s infinite;
}

.record-btn.connecting-websocket .microphone-icon {
    animation: pulse-icon 1.5s infinite;
}

.record-btn.recording .microphone-icon {
    animation: pulse-icon 0.8s infinite;
    transform: scale(1.1);
}

.record-btn:not(:disabled):hover .microphone-icon {
    transform: scale(1.05);
}

@keyframes pulse-icon {
    0% { 
        transform: scale(1);
        opacity: 1;
    }
    50% { 
        transform: scale(1.1);
        opacity: 0.8;
    }
    100% { 
        transform: scale(1);
        opacity: 1;
    }
}

@keyframes pulse-connecting {
    0% { 
        transform: scale(1);
        box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3);
    }
    50% { 
        transform: scale(1.02);
        box-shadow: 0 6px 16px rgba(255, 152, 0, 0.5);
    }
    100% { 
        transform: scale(1);
        box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3);
    }
}

.status-info {
    margin: 15px 0;
}

.status {
    padding: 8px 12px;
    border-radius: 20px;
    font-size: 0.9rem;
    font-weight: 500;
    margin: 5px 0;
}

.status.connected {
    background: #e8f5e8;
    color: #2e7d32;
}

.status.disconnected {
    background: #ffebee;
    color: #c62828;
}

.status.connecting {
    background: #fff3e0;
    color: #f57c00;
}

.connection-progress {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
}

.progress-bar {
    width: 200px;
    height: 6px;
    background: #e0e0e0;
    border-radius: 3px;
    overflow: hidden;
    position: relative;
}

.progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #4CAF50, #8BC34A);
    border-radius: 3px;
    transition: width 0.3s ease;
    position: relative;
}

.progress-fill::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
    animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
}

.progress-text {
    font-size: 0.9rem;
    font-weight: 500;
}

.recording-info {
    margin: 15px 0;
}

.timer {
    font-size: 1.5rem;
    font-weight: bold;
    color: #f44336;
}

.result-container {
    margin: 20px 0;
}

.result-text {
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 10px;
    padding: 20px;
    min-height: 100px;
    font-size: 1.1rem;
    line-height: 1.6;
    color: #333;
    white-space: pre-wrap;
}

.debug-info {
    margin: 20px 0;
    padding: 15px;
    background: #f0f8ff;
    border: 1px solid #b3d9ff;
    border-radius: 8px;
    font-size: 0.9rem;
}

.debug-info h4 {
    margin: 0 0 10px 0;
    color: #0066cc;
}

.debug-item {
    margin: 5px 0;
    padding: 3px 0;
}
</style>
